import { makeAutoObservable, runInAction } from 'mobx';

import type { WellIndexType, WellLog } from '../api/IWellDirectoryService';
import type { WellDirectoryService } from '../api/WellDirectoryService';
import type { WellDirectoryServiceStompClient, WellStructureChangeStatusMessage } from '../stompTypes';
import { arraysEqual, arraySum } from '../utils';

export class WellLogs {
  inProgress = false;

  private logAbortController = new AbortController();

  list: WellLog[] = [];

  runNumbers: number[][] = [];

  wellId: number | null = null;

  wellboreId: number | null = null;

  constructor(
    private wellDirectoryService: WellDirectoryService,
    private wellDirectoryServiceStompClient: WellDirectoryServiceStompClient,
  ) {
    makeAutoObservable(this);
  }

  get allCurves() {
    return this.list.flatMap((log) => log.curves);
  }

  async fetchWellLogs(wellId: number, wellboreId: number, index: WellIndexType, abortController?: AbortController) {
    this.inProgress = true;

    try {
      const data = await this.wellDirectoryService.getLogs(wellId, wellboreId, index, abortController);
      abortController?.signal.throwIfAborted();

      runInAction(() => {
        this.setLogs(wellId, wellboreId, data);
      });

      return data;
    } catch (e: unknown) {
      if (!abortController?.signal.aborted) {
        console.error('ErrorToFetch', e);
      }
      throw e;
    } finally {
      runInAction(() => {
        if (!abortController?.signal.aborted) {
          this.inProgress = false;
        }
      });
    }
  }

  setLogs(wellId: number, wellboreId: number, data: WellLog[]) {
    this.clear();
    this.wellId = wellId;
    this.wellboreId = wellboreId;
    this.list = data.reduce<WellLog[]>((acc, curr) => {
      if (curr.curves.length > 0) {
        return acc.concat({ ...curr });
      }
      return acc;
    }, []);
    this.runNumbers = data.reduce<number[][]>((acc, log) => {
      if (!acc.some((numbers) => arraysEqual(numbers, log.runNumber))) {
        acc.push(log.runNumber);
      }
      return acc;
    }, []);
    this.runNumbers.sort((a, b) => arraySum(a) - arraySum(b));
    this.wellDirectoryServiceStompClient.subscribe(`/wellObjects/${wellId}`, this.messageResolver);
  }

  fetchLog(logId: number) {
    if (!this.wellId || !this.wellboreId) {
      return Promise.reject('No well ID');
    }

    return this.wellDirectoryService.getLogById(this.wellId, this.wellboreId, logId, this.logAbortController);
  }

  messageResolver = async (message: WellStructureChangeStatusMessage) => {
    if (message.entityType === 'LOG') {
      if (message.changeType === 'OBJECT_CREATE' || message.changeType === 'DATA_UPDATED') {
        const controller = this.logAbortController;
        try {
          const log = await this.fetchLog(message.entityId);
          controller.signal.throwIfAborted();
          this.updateLog(log);
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('ErrorToFetch', error);
          }
        }
      } else if (message.changeType === 'OBJECT_DELETE') {
        this.list = this.list.filter((log) => log.id !== message.entityId);
      }
    }
  };

  updateLog(log: WellLog) {
    const index = this.list.findIndex((item) => item.id === log.id);

    if (index !== -1) {
      this.list[index] = log;
    } else {
      this.list.push(log);
    }
  }

  clear() {
    this.list = [];
    this.runNumbers = [];
    this.logAbortController.abort();
    this.logAbortController = new AbortController();
    if (this.wellId != null) {
      this.wellDirectoryServiceStompClient.unsubscribe(`/wellObjects/${this.wellId}`, this.messageResolver);
    }
    this.wellId = null;
    this.wellboreId = null;
  }

  destroy() {
    this.clear();
  }
}
